import { useState, useEffect } from 'react';
import { Package, CheckCircle, DollarSign, XCircle } from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { getLoadStats } from '../../../entities/load';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

export function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getLoadStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to load dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="p-6 w-full flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Loading dashboard...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6 w-full flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Failed to load dashboard data.</p>
      </div>
    );
  }

  const sc = stats.statusCounts || {};
  const loadStatusData = {
    labels: ['Delivered', 'In Transit', 'Awaiting Pickup', 'Cancelled'],
    datasets: [
      {
        data: [sc['Delivered'] || 0, sc['In Transit'] || 0, sc['Awaiting Pickup'] || 0, sc['Cancelled'] || 0],
        backgroundColor: ['#22c55e', '#3b82f6', '#6b7280', '#ef4444'],
        borderWidth: 1,
      },
    ],
  };

  const revenueTrendData = {
    labels: (stats.monthlyRevenue || []).map(m => m.month),
    datasets: [
      {
        label: 'Revenue',
        data: (stats.monthlyRevenue || []).map(m => m.revenue),
        backgroundColor: '#3b82f6',
        borderRadius: 6,
      },
    ],
  };

  return (
    <div className="p-6 w-full">
      {/* Top Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">Active Loads</p>
            <Package className="w-6 h-6 text-blue-500" />
          </div>
          <h3 className="text-2xl font-bold mt-2">{stats.activeLoads}</h3>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">Completed Loads</p>
            <CheckCircle className="w-6 h-6 text-green-500" />
          </div>
          <h3 className="text-2xl font-bold mt-2">{stats.completedLoads}</h3>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">Total Revenue</p>
            <DollarSign className="w-6 h-6 text-green-500" />
          </div>
          <h3 className="text-2xl font-bold mt-2">${Number(stats.totalRevenue || 0).toLocaleString()}</h3>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">Cancellation Rate</p>
            <XCircle className="w-6 h-6 text-red-500" />
          </div>
          <h3 className="text-2xl font-bold mt-2">{stats.cancellationRate}%</h3>
        </div>
      </div>

      {/* Middle Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Donut Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-4">Load Status Distribution</h4>
          <Doughnut data={loadStatusData} />
          <ul className="mt-4 space-y-1 text-sm text-gray-600">
            <li>Delivered: {sc['Delivered'] || 0}</li>
            <li>In Transit: {sc['In Transit'] || 0}</li>
            <li>Awaiting Pickup: {sc['Awaiting Pickup'] || 0}</li>
            <li>Cancelled: {sc['Cancelled'] || 0}</li>
          </ul>
        </div>

        {/* Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-4">Monthly Revenue Trend</h4>
          <Bar data={revenueTrendData} options={{ plugins: { legend: { display: false } } }} />
        </div>
      </div>

      {/* Bottom KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">On-Time Delivery</p>
          <p className="text-lg font-semibold">{stats.onTimeDelivery}%</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Driver Utilization</p>
          <p className="text-lg font-semibold">{stats.driverUtilization}%</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Fuel Efficiency</p>
          <p className="text-lg font-semibold">7.2 MPG</p>
        </div>
      </div>
    </div>
  );
}
